import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, ArrowRight, ArrowLeft } from 'lucide-react';
import InkPhDLogo from '@/components/InkPhDLogo';
import AppFooter from '@/components/AppFooter';
import SeoHead from '@/components/SeoHead';

const LANG_KEY = 'inkphd-guide-lang';

const COPY = {
  en: {
    title: 'Guides',
    subtitle: 'Two working methods, written for the way research is actually produced.',
    open: 'Open guide',
    back: 'Back to my projects',
    cards: [
      {
        to: '/guide', icon: 'article', name: 'Writing a scientific article',
        desc: 'The complete workflow for a journal manuscript, review, protocol or abstract — '
          + 'project setup, evidence library, structure, generation, verified citations, quality '
          + 'checks, readiness and export. 15 steps + a 30-minute quick start.',
      },
      {
        to: '/guide/dissertation', icon: 'thesis', name: 'Writing a doctoral dissertation',
        desc: 'A month-by-month method for a medical PhD: composite reporting guidelines, the '
          + 'chapter tree, the tiered literature review, Methods and statistics per sub-study, '
          + 'defence rehearsal, the abstract booklet and a realistic 12-month plan.',
      },
    ],
  },
  bg: {
    title: 'Ръководства',
    subtitle: 'Два работни метода, написани според това как реално се прави наука.',
    open: 'Отвори ръководството',
    back: 'Обратно към проектите',
    cards: [
      {
        to: '/guide', icon: 'article', name: 'Как се пише научна статия',
        desc: 'Пълният процес за ръкопис, обзор, протокол или резюме — създаване на проект, '
          + 'библиотека с източници, структура, генериране, проверени цитати, проверки за '
          + 'качество, готовност и експорт. 15 стъпки + бърз старт за 30 минути.',
      },
      {
        to: '/guide/dissertation', icon: 'thesis', name: 'Как се пише докторска дисертация',
        desc: 'Метод месец по месец за медицински доктурант: композитни отчетни насоки, дърво от '
          + 'глави, литературен обзор на нива, методи и статистика по подизследвания, репетиция '
          + 'на защитата, автореферат и реалистичен 12-месечен план.',
      },
    ],
  },
};

export default function GuidesIndexPage() {
  const [lang, setLang] = useState('en');

  useEffect(() => {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === 'bg' || saved === 'en') setLang(saved);
  }, []);

  const setLanguage = (next) => {
    setLang(next);
    window.localStorage.setItem(LANG_KEY, next);
  };

  const t = COPY[lang];

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead title={`InkPhD · ${t.title}`} path="/guides" description={t.subtitle} />

      <header className="border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="guides-back-link">
            <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">{t.back}</span>
          </Link>
          <div className="flex items-center gap-3">
            <InkPhDLogo size="sm" />
            <div className="flex rounded-full border border-border overflow-hidden" data-testid="guides-lang-toggle">
              {['en', 'bg'].map((code) => (
                <button key={code} onClick={() => setLanguage(code)}
                  data-testid={`guides-lang-${code}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors
                    ${lang === code ? 'bg-[#0b1a35] text-white' : 'text-muted-foreground hover:bg-muted'}`}>
                  {code}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 py-12 sm:py-20" data-testid="guides-index-page">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight mb-4"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          {t.title}
        </h1>
        <p className="text-base text-muted-foreground mb-12 max-w-2xl">{t.subtitle}</p>

        <div className="grid md:grid-cols-2 gap-5">
          {t.cards.map((c) => {
            const Icon = c.icon === 'thesis' ? GraduationCap : BookOpen;
            return (
              <Link key={c.to} to={c.to} data-testid={`guides-card-${c.icon}`}
                className="group rounded-2xl border border-border p-6 hover:border-[#1d4ed8]/50
                  hover:shadow-lg transition-all bg-card/40 flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-[#1d4ed8]/10 dark:bg-[#1d4ed8]/20 flex items-center
                  justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#1d4ed8] dark:text-[#7aa9ff]" />
                </div>
                <h2 className="text-lg font-serif font-semibold mb-2 group-hover:text-[#1d4ed8]
                  dark:group-hover:text-[#7aa9ff] transition-colors"
                  style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                  {c.name}
                </h2>
                <p className="text-[13.5px] text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
                <span className="mt-5 text-[12px] font-semibold inline-flex items-center gap-1
                  text-[#1d4ed8] dark:text-[#7aa9ff]">
                  {t.open} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </main>

      <AppFooter lang="en" />
    </div>
  );
}
